const API_BASE = '/api/v1';

function getCsrfToken() {
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

class ApiService {
  constructor() {
    this._refreshPromise = null;
  }

  async _tryRefresh() {
    if (this._refreshPromise) return this._refreshPromise;
    this._refreshPromise = fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }).then(r => {
      this._refreshPromise = null;
      return r.ok;
    }).catch(() => {
      this._refreshPromise = null;
      return false;
    });
    return this._refreshPromise;
  }

  async request(endpoint, options = {}) {
    const { skipAuthRedirect, _isRetry, ...fetchOptions } = options;
    const headers = {
      'Content-Type': 'application/json',
      ...fetchOptions.headers,
    };

    if (fetchOptions.method && fetchOptions.method !== 'GET') {
      const csrf = getCsrfToken();
      if (csrf) headers['X-CSRF-Token'] = csrf;
    }

    const config = {
      ...fetchOptions,
      headers,
      credentials: 'include',
    };

    const response = await fetch(`${API_BASE}${endpoint}`, config);

    if (!response.ok) {
      if (response.status === 401 && !_isRetry) {
        if (skipAuthRedirect) {
          // Auth-Endpunkte (login, register...): echte Fehlermeldung lesen, kein Refresh-Versuch
          const error = await response.json().catch(() => ({ error: 'Unauthorized' }));
          throw new Error(error.error || 'Unauthorized');
        }
        const refreshed = await this._tryRefresh();
        if (refreshed) {
          return this.request(endpoint, { ...options, _isRetry: true });
        }
        window.location.href = '/login';
        throw new Error('Unauthorized');
      }
      if (response.status === 401) {
        if (!skipAuthRedirect) window.location.href = '/login';
        throw new Error('Unauthorized');
      }
      const error = await response.json().catch(() => ({ error: 'Request failed' }));
      const err = new Error(error.error || `HTTP ${response.status}`);
      if (error.code)  err.code  = error.code;
      if (error.email) err.email = error.email;
      throw err;
    }

    return response.json();
  }

  // Öffentliche Rezepte (kein Login nötig — Demo)
  async getPublicRecipes({ limit = 20, offset = 0, category, search } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (category) params.append('category', category);
    if (search)   params.append('search', search);
    return this.request(`/recipes/public?${params}`, { skipAuthRedirect: true });
  }

  async getPublicRecipe(id) {
    return this.request(`/recipes/public/${id}`, { skipAuthRedirect: true });
  }

  async getRecipeBySlug(slug) {
    return this.request(`/recipes/r/${slug}`, { skipAuthRedirect: true });
  }

  // Auth
  async login(username, password) {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
      skipAuthRedirect: true,
    });
  }

  async logout() {
    await this.request('/auth/logout', { method: 'POST' });
  }

  async refreshSession() {
    return this._tryRefresh();
  }

  async getCurrentUser() {
    return this.request('/auth/me', { skipAuthRedirect: true });
  }

  // Recipes
  async getRecipes(params = {}) {
    const filtered = Object.fromEntries(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '')
    );
    const query = new URLSearchParams(filtered).toString();
    return this.request(`/recipes${query ? '?' + query : ''}`);
  }

  async getRecipe(id) {
    return this.request(`/recipes/${id}`);
  }

  async createRecipe(data) {
    return this.request('/recipes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateRecipe(id, data) {
    return this.request(`/recipes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRecipe(id) {
    return this.request(`/recipes/${id}`, {
      method: 'DELETE',
    });
  }

  async searchRecipes(query) {
    return this.request(`/recipes/search?q=${encodeURIComponent(query)}`);
  }

  async getCategories() {
    return this.request('/recipes/categories', { skipAuthRedirect: true });
  }

  async getTags(publicOnly = false) {
    return this.request(`/recipes/tags${publicOnly ? '?public_only=true' : ''}`, { skipAuthRedirect: true });
  }

  // Users (Admin only)
  async getUsers() {
    return this.request('/users');
  }

  async createUser(data) {
    return this.request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateUser(id, data) {
    return this.request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteUser(id) {
    return this.request(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  async updatePermissions(id, permissions) {
    return this.request(`/users/${id}/permissions`, {
      method: 'PUT',
      body: JSON.stringify(permissions),
    });
  }

  async getUserStats() {
    return this.request('/users/stats');
  }

  // Layouts / Pages
  async getLayouts() {
    return this.request('/layouts');
  }

  async getLayout(slug) {
    return this.request(`/layouts/${slug}`, { skipAuthRedirect: true });
  }

  async saveLayout(slug, name, layout_json, is_global = false) {
    return this.request(`/layouts/${slug}`, {
      method: 'PUT',
      body: JSON.stringify({ name, layout_json, is_global }),
    });
  }

  async deleteLayout(slug) {
    return this.request(`/layouts/${slug}`, { method: 'DELETE' });
  }

  // Upload
  async uploadImage(file, title = '', detectMultiple = false) {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    formData.append('detect_multiple', detectMultiple ? 'true' : 'false');
    formData.append('process_now', 'true');  // Immer sofort verarbeiten!

    const csrf = getCsrfToken();
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      credentials: 'include',
      headers: csrf ? { 'X-CSRF-Token': csrf } : {},
      body: formData,
    });

    if (!response.ok) {
      if (response.status === 401) {
        window.location.href = '/login';
        return;
      }
      if (response.status === 429) {
        const error = await response.json().catch(() => null);
        throw new Error(error?.error || 'Monatliches Scan-Limit erreicht');
      }
      const error = await response.json().catch(() => ({ error: 'Upload failed' }));
      throw new Error(error.error || `HTTP ${response.status}`);
    }

    return response.json();
  }

  async getUploadFormats() {
    return this.request('/upload/formats');
  }

  // Module & Pakete
  async getMyModules() {
    return this.request('/modules');
  }

  async getPublicModules() {
    const response = await fetch(`${API_BASE}/modules/public`);
    return response.json();
  }

  async getAllModules() {
    return this.request('/modules/all');
  }

  async createModule(data) {
    return this.request('/modules', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async toggleModule(name, enabled) {
    return this.request(`/modules/${name}`, {
      method: 'PUT',
      body: JSON.stringify({ enabled }),
    });
  }

  async deleteModule(name) {
    return this.request(`/modules/${name}`, { method: 'DELETE' });
  }

  async getPackages() {
    return this.request('/modules/packages');
  }

  async updatePackage(id, data) {
    return this.request(`/modules/packages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async setPackageModules(id, modules) {
    return this.request(`/modules/packages/${id}/modules`, {
      method: 'PUT',
      body: JSON.stringify({ modules }),
    });
  }

  async setUserPackage(userId, packageId) {
    return this.request(`/modules/users/${userId}/package`, {
      method: 'PUT',
      body: JSON.stringify({ package_id: packageId }),
    });
  }

  // Regionen
  async getAllRegions() {
    return this.request('/regions/all');
  }

  async saveRegion(name, data) {
    return this.request(`/regions/${name}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteRegion(name) {
    return this.request(`/regions/${name}`, { method: 'DELETE' });
  }

  async getModulePages() {
    return this.request('/regions/module-pages');
  }

  async setModulePages(moduleName, pages) {
    return this.request(`/regions/module-pages/${moduleName}`, {
      method: 'PUT',
      body: JSON.stringify({ pages }),
    });
  }

  // Registrierung
  async register(data) {
    return this.request('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuthRedirect: true,
    });
  }

  async verifyEmail(email, code) {
    return this.request('/auth/verify-email', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
      skipAuthRedirect: true,
    });
  }

  async resendVerification(email) {
    return this.request('/auth/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuthRedirect: true,
    });
  }

  async forgotPassword(email) {
    return this.request('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuthRedirect: true,
    });
  }

  async resetPassword(token, password) {
    return this.request('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      skipAuthRedirect: true,
    });
  }

  // OCR-Korrekturen
  async saveCorrections(recipeId, corrections) {
    return this.request('/corrections', {
      method: 'POST',
      body: JSON.stringify({ recipe_id: recipeId, corrections }),
    });
  }

  async getCorrections(limit = 200, offset = 0) {
    return this.request(`/corrections?limit=${limit}&offset=${offset}`);
  }

  async exportCorrections() {
    return this.request('/corrections/export');
  }

  async analyzeCorrections(force = false) {
    return this.request('/corrections/analyze', {
      method: 'POST',
      body: JSON.stringify({ force }),
    });
  }

  async getSchedule() {
    return this.request('/corrections/schedule');
  }

  async saveSchedule(data) {
    return this.request('/corrections/schedule', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async confirmOnboarding() {
    return this.request('/auth/confirm-onboarding', { method: 'POST' });
  }

  // User Profile
  async getProfile() {
    return this.request('/user/profile');
  }

  async updateProfile(data) {
    return this.request('/user/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async changePassword(old_password, new_password, new_password2) {
    return this.request('/user/change-password', {
      method: 'POST',
      body: JSON.stringify({ old_password, new_password, new_password2 }),
    });
  }

  async getPreferences() {
    return this.request('/user/preferences');
  }

  async updatePreferences(data) {
    return this.request('/user/preferences', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async uploadRecipeCover(recipeId, file) {
    const formData = new FormData();
    formData.append('file', file);
    const csrf = getCsrfToken();
    const response = await fetch(`${API_BASE}/recipes/${recipeId}/cover`, {
      method: 'POST', credentials: 'include', headers: csrf ? { 'X-CSRF-Token': csrf } : {}, body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async uploadStepImage(recipeId, stepIdx, file) {
    const formData = new FormData();
    formData.append('file', file);
    const csrf = getCsrfToken();
    const response = await fetch(`${API_BASE}/recipes/${recipeId}/steps/${stepIdx}/image`, {
      method: 'POST', credentials: 'include', headers: csrf ? { 'X-CSRF-Token': csrf } : {}, body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  async uploadAvatar(file) {
    const formData = new FormData();
    formData.append('file', file);
    const csrf = getCsrfToken();
    const response = await fetch(`${API_BASE}/user/avatar`, {
      method: 'POST', credentials: 'include', headers: csrf ? { 'X-CSRF-Token': csrf } : {}, body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || `HTTP ${response.status}`);
    }
    return response.json();
  }

  // Kühlschrank-Scan
  async fridgeScan(files) {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach(f => formData.append('files', f));
    const csrf = getCsrfToken();
    const response = await fetch(`${API_BASE}/fridge/scan`, {
      method: 'POST', credentials: 'include', headers: csrf ? { 'X-CSRF-Token': csrf } : {}, body: formData,
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw Object.assign(new Error(err.error || `HTTP ${response.status}`), { code: err.code });
    }
    return response.json();
  }

  async fridgeScanStream(files, onEvent, timeoutMs = 90000, aufwand = '', aufwandText = '') {
    const formData = new FormData();
    const fileArray = Array.isArray(files) ? files : [files];
    fileArray.forEach(f => formData.append('files', f));
    if (aufwand) formData.append('aufwand', aufwand);
    if (aufwandText) formData.append('aufwand_text', aufwandText);
    const csrf = getCsrfToken();

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    let reader;
    try {
      const response = await fetch(`${API_BASE}/fridge/scan-stream`, {
        method: 'POST', credentials: 'include',
        headers: csrf ? { 'X-CSRF-Token': csrf } : {},
        body: formData,
        signal: controller.signal,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw Object.assign(new Error(err.error || `HTTP ${response.status}`), { code: err.code });
      }
      reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (line.startsWith('data: ')) {
            try { onEvent(JSON.parse(line.slice(6))); } catch {}
          }
        }
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        throw new Error('Scan-Timeout — bitte erneut versuchen oder weniger Bilder verwenden.');
      }
      throw err;
    } finally {
      clearTimeout(timer);
      reader?.cancel().catch(() => {});
    }
  }

  async saveFridgeAiRecipe(recipe) {
    return this.request('/fridge/save-ai', { method: 'POST', body: JSON.stringify({ recipe }) });
  }

  async getFridgeQuota() {
    return this.request('/fridge/quota');
  }

  async getFridgeExpiring() {
    return this.request('/fridge/expiring');
  }

  async getFridgeItems() {
    return this.request('/fridge/items');
  }

  async updateFridgeItemMenge(id, menge) {
    return this.request(`/fridge/items/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ menge }),
    });
  }

  async saveFridgeFeedback({ recipe_title, recipe_category, liked, reason_tag, note }) {
    return this.request('/fridge/feedback', {
      method: 'POST',
      body: JSON.stringify({ recipe_title, recipe_category, liked, reason_tag, note }),
    });
  }

  async getFridgeFeedback(page = 1, perPage = 20) {
    return this.request(`/fridge/feedback?page=${page}&per_page=${perPage}`);
  }

  async updateFridgeFeedback(id, data) {
    return this.request(`/fridge/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteFridgeFeedback(id) {
    return this.request(`/fridge/feedback/${id}`, { method: 'DELETE' });
  }

  async getAdminFridgeQuota(userId) {
    return this.request(`/fridge/admin/quota/${userId}`);
  }

  async resetAdminFridgeQuota(userId) {
    return this.request(`/fridge/admin/reset-quota/${userId}`, { method: 'POST' });
  }

  // Zutaten-Wörterbuch
  async getIngredients() {
    return this.request('/ingredients');
  }

  async validateIngredient(name) {
    return this.request('/ingredients/validate', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  // Quota
  async getUploadQuota() {
    return this.request('/upload/quota');
  }

  async urlImport(url, source = '') {
    return this.request('/upload/url-import', {
      method: 'POST',
      body: JSON.stringify({ url, source, save: false }),
    });
  }

  async importRecipeCoverFromUrl(recipeId, imageUrl) {
    return this.request(`/recipes/${recipeId}/cover-from-url`, {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    });
  }

  async uploadStepImageFromUrl(recipeId, stepIdx, imageUrl) {
    return this.request(`/recipes/${recipeId}/steps/${stepIdx}/image-from-url`, {
      method: 'POST',
      body: JSON.stringify({ image_url: imageUrl }),
    });
  }

  async getUserQuota(userId) {
    return this.request(`/users/${userId}/quota`);
  }

  async setUserQuota(userId, overrideOrNull) {
    return this.request(`/users/${userId}/quota`, {
      method: 'PUT',
      body: JSON.stringify({ scan_limit_override: overrideOrNull }),
    });
  }

  async getUser(id) {
    return this.request(`/users/${id}`);
  }

  async createShareLink(recipeId) {
    return this.request(`/recipes/${recipeId}/share`, { method: 'POST' });
  }

  async markRecipeCooked(recipeId) {
    return this.request(`/recipes/${recipeId}/cooked`, { method: 'POST' });
  }

  async getSharedRecipeByToken(token) {
    return this.request(`/share/${token}`, { skipAuthRedirect: true });
  }

  async getPublicLayout(slug) {
    return this.request(`/layouts/${slug}`, { skipAuthRedirect: true });
  }

  async getPublicRegions(page = '__all__') {
    return this.request(`/regions?page=${encodeURIComponent(page)}`, { skipAuthRedirect: true });
  }

  async revokeShareLink(recipeId) {
    return this.request(`/recipes/${recipeId}/share`, { method: 'DELETE' });
  }

  async processBox(filename, box, title, isPublic) {
    return this.request('/upload/process-box', {
      method: 'POST',
      body: JSON.stringify({ filename, box, title, is_public: isPublic }),
    });
  }

  // 2FA / TOTP
  async totp2faLogin(temp_token, code) {
    return this.request('/auth/2fa/login', {
      method: 'POST',
      body: JSON.stringify({ temp_token, code }),
      skipAuthRedirect: true,
    });
  }

  async totpSetup() {
    return this.request('/auth/2fa/setup');
  }

  async totpEnable(code) {
    return this.request('/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async totpDisable(password, code) {
    return this.request('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password, code }),
    });
  }

  async totpStatus() {
    return this.request('/auth/2fa/status');
  }

  async totpGetSettings() {
    return this.request('/auth/2fa/settings');
  }

  async totpSaveSettings(totp_required) {
    return this.request('/auth/2fa/settings', {
      method: 'POST',
      body: JSON.stringify({ totp_required }),
    });
  }

  async deleteAccount(password) {
    return this.request('/user/account', {
      method: 'DELETE',
      body: JSON.stringify({ password, confirm: true }),
    });
  }

  async exportUserData() {
    const csrf = getCsrfToken();
    const headers = { 'Content-Type': 'application/json' };
    if (csrf) headers['X-CSRF-Token'] = csrf;
    const response = await fetch(`${API_BASE}/user/export`, {
      credentials: 'include',
      headers,
    });
    if (!response.ok) throw new Error('Export fehlgeschlagen');
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'strixly-daten.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  async submitFeedback(type, name, email, message) {
    return this.request('/feedback', {
      method: 'POST',
      body: JSON.stringify({ type, name, email, message }),
      skipAuthRedirect: true,
    });
  }

  async getAuditLog({ limit = 100, offset = 0, user_id, action } = {}) {
    const params = new URLSearchParams({ limit, offset });
    if (user_id) params.append('user_id', user_id);
    if (action)  params.append('action', action);
    return this.request(`/admin/audit-log?${params}`);
  }

  // ── Einkaufsliste (Backend-Sync) ──────────────────────────────────────────
  async getShoppingList() {
    return this.request('/shopping', { skipAuthRedirect: true });
  }

  async addShoppingItems(items) {
    return this.request('/shopping/batch', {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async toggleShoppingItem(id) {
    return this.request(`/shopping/${id}`, { method: 'PATCH' });
  }

  async deleteShoppingItem(id) {
    return this.request(`/shopping/${id}`, { method: 'DELETE' });
  }

  async clearCheckedShoppingItems() {
    return this.request('/shopping/checked', { method: 'DELETE' });
  }

  async clearAllShoppingItems() {
    return this.request('/shopping/all', { method: 'DELETE' });
  }
}

const api = new ApiService();
export default api;